import { Button } from "design-system/atoms/Button"
import { Checkbox } from "design-system/atoms/Checkbox"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "design-system/atoms/Dialog"
import { Label } from "design-system/atoms/Label"
import { Loader2 } from "lucide-react"
import { useId, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  storeShouldSkipDismissConfirmation,
  useDismissKnowledgeSuggestionMutation,
} from "../lib/useDismissKnowledgeSuggestionMutation"

type Props = {
  gapId: string
  onClose: () => void
}

export function KnowledgeSuggestionDismissDialog({ gapId, onClose }: Props) {
  const { t } = useTranslation()
  const checkboxId = useId()
  const [shouldSkipConfirmation, setShouldSkipConfirmation] = useState(false)
  const { mutate, isPending, isError } = useDismissKnowledgeSuggestionMutation()

  function onConfirm() {
    storeShouldSkipDismissConfirmation(shouldSkipConfirmation)
    mutate(gapId, { onSuccess: onClose })
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle>{t("knowledge.suggestions.dismissDialog.title")}</DialogTitle>
          <DialogDescription>
            {t("knowledge.suggestions.dismissDialog.description")}
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2">
          <Checkbox
            id={checkboxId}
            checked={shouldSkipConfirmation}
            onCheckedChange={(checked) => setShouldSkipConfirmation(checked === true)}
          />
          <Label htmlFor={checkboxId} className="text-sm font-normal">
            {t("knowledge.suggestions.dismissDialog.skipConfirmation")}
          </Label>
        </div>
        {isError && (
          <p role="alert" className="text-sm text-destructive">
            {t("knowledge.suggestions.dismissError")}
          </p>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
            {t("knowledge.suggestions.dismissDialog.cancel")}
          </Button>
          <Button type="button" onClick={onConfirm} disabled={isPending}>
            {isPending && <Loader2 aria-hidden="true" className="mr-2 h-4 w-4 animate-spin" />}
            {t("knowledge.suggestions.dismiss")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
